
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getUserGymId } from '@/utils/gymAuth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ScheduledClass } from "@/types/types";
import { Course } from "@/types/course.types";

interface EditClassDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  classInfo: ScheduledClass | null;
  date: Date;
  onSave: (values: {
    id?: string;
    name: string;
    instructor: string;
    categoryId: string;
    capacity: number;
    duration: string;
    time: string;
    courseId?: string;
  }, date: Date) => Promise<boolean>;
}

const formSchema = z.object({
  id: z.string().optional(),
  className: z.string().min(2, {
    message: "Class name must be at least 2 characters.",
  }),
  instructor: z.string().min(2, {
    message: "Instructor name must be at least 2 characters.",
  }),
  time: z.string().min(1, {
    message: "Time must be specified",
  }),
  duration: z.string().min(1, {
    message: "Duration must be specified",
  }),
  categoryId: z.string().min(1, {
    message: "Category must be specified",
  }),
  capacity: z.coerce.number().min(1, {
    message: "Capacity must be at least 1 person",
  }),
  courseId: z.string().optional(),
});

// Define proper type for courses


export function EditClassDialog({
  isOpen,
  onOpenChange,
  classInfo,
  date,
  onSave,
}: EditClassDialogProps) {
  const { t } = useTranslation(["schedule", "common"]);
  const gymId = getUserGymId();
  const [isLoading, setIsLoading] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [coachNames, setCoachNames] = useState<string[]>([]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: classInfo?.id || undefined,
      className: classInfo?.name || "",
      // Plus de repli sur « Default Instructor » : le champ est saisi.
      instructor: classInfo?.instructor || "",
      time: classInfo?.time || "",
      duration: classInfo?.duration || "60",
      categoryId: classInfo?.categoryId || "",
      capacity: classInfo?.capacity || 0,
      courseId: classInfo?.courseId || undefined,
    },
  });
  
  // Fetch courses and categories
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          // Import wrapper at runtime to avoid circular deps
          const { getCourses, getCategories } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');
          
          // Fetch courses
          const coursesData = await getCourses(gymId);
          
          // Transform the courses data to match our Course type
          const transformedCourses = coursesData.map(course => ({
            id: course.id,
            name: course.name,
            category_id: course.category_id,
            default_capacity: course.default_capacity,
            default_duration: course.default_duration.toString() // Convert number to string
          }));
          
          setCourses(transformedCourses);
          
          // Fetch categories
          const categoriesData = await getCategories(gymId);
          setCategories(categoriesData || []);

          // Suggestions de coachs pour le champ « Coach ».
          try {
            const { getAllCoaches } = await import('@/store/api/coaches/coachFetchOperations');
            const coachesData = await getAllCoaches();
            setCoachNames(
              (coachesData || [])
                .map((c) => `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim())
                .filter(Boolean)
            );
          } catch (coachError) {
            // Simple aide à la saisie : son échec ne bloque pas l'édition.
            console.error('Error fetching coaches for instructor field:', coachError);
          }

        } catch (error) {
          console.error('Error fetching data:', error);
          toast.error(t('errorFetchingData', { ns: 'common', defaultValue: 'Failed to load data' }));
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchData();
    }
  }, [isOpen, t]);
  
  // Handle course selection
  const handleCourseSelect = (courseId: string) => {
    const selectedCourse = courses.find(course => course.id === courseId);
    if (!selectedCourse) return;
    
    form.setValue('courseId', courseId);
    form.setValue('className', selectedCourse.name);
    
    // Update category if available
    if (selectedCourse.category_id) {
      form.setValue('categoryId', selectedCourse.category_id);
    }
    
    // Update capacity and duration if available
    if (selectedCourse.default_capacity) {
      form.setValue('capacity', selectedCourse.default_capacity);
    }
    
    if (selectedCourse.default_duration) {
      form.setValue('duration', selectedCourse.default_duration);
    }
  };

  // Update form when classInfo changes
  // On édite UNE séance : ses valeurs propres (capacité, durée, horaire) font
  // foi. Un effet appelait auparavant `handleCourseSelect` dès que la liste des
  // cours finissait de charger et réécrivait capacité et durée avec les valeurs
  // par défaut du cours — une séance passée à 25 places repassait à 10 au
  // premier enregistrement. Les valeurs par défaut du cours ne s'appliquent
  // désormais que si l'utilisateur change explicitement de cours dans le menu.
  // Brouillon par séance (edit-class:{id}) : le dialog est monté à chaque
  // ouverture, mergeDraftOver superpose la frappe interrompue aux valeurs de
  // la séance. `id` exclu (identité, pas une saisie).
  const { clearDraft, mergeDraftOver } = useFormDraft<z.infer<typeof formSchema>>(
    "edit-class",
    form,
    { entityId: classInfo?.id ?? null, restoreOnMount: false, exclude: ["id"] }
  );

  useEffect(() => {
    if (classInfo) {
      form.reset(mergeDraftOver({
        id: classInfo.id,
        className: classInfo.name,
        instructor: classInfo.instructor,
        time: classInfo.time,
        duration: classInfo.duration,
        categoryId: classInfo.categoryId,
        capacity: classInfo.capacity,
        courseId: classInfo.courseId,
      }));
    }
  }, [classInfo, form, mergeDraftOver]);
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const success = await onSave({
        id: values.id,
        name: values.className,
        instructor: values.instructor,
        categoryId: values.categoryId,
        capacity: values.capacity,
        duration: values.duration,
        time: values.time,
        courseId: values.courseId
      }, date);
      
      if (success) {
        toast.success(t('classUpdatedSuccess', { ns: 'schedule' }));
        clearDraft();
        onOpenChange(false);
      } else {
        toast.error(t('errorUpdatingClass', { ns: 'common', defaultValue: 'Failed to update class' }));
      }
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error(t('errorUpdatingClass', { ns: 'common', defaultValue: 'Failed to update class' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("editClass", { ns: "schedule" })}</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Course selection dropdown */}
            <FormField
              control={form.control}
              name="courseId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("course", { ns: "common" })}</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={handleCourseSelect}
                    disabled={isLoading}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("selectCourse", { defaultValue: "Select a course", ns: "common" })} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="instructor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("instructor", { ns: "common", defaultValue: "Coach" })}</FormLabel>
                  <FormControl>
                    <Input
                      list="edit-class-coach-suggestions"
                      placeholder={t("instructorPlaceholder", { ns: "common", defaultValue: "Nom du coach" })}
                      {...field}
                    />
                  </FormControl>
                  <datalist id="edit-class-coach-suggestions">
                    {coachNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col sm:flex-row gap-4">
              <FormField
                control={form.control}
                name="time"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("time", { ns: "common" })}</FormLabel>
                    <FormControl>
                      <Input 
                        type="time"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("duration", { ns: "common" })}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder={t("durationPlaceholder", { defaultValue: "Duration in minutes", ns: "common" })}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <FormField
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("category", { ns: "common" })}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("categoryPlaceholder", { defaultValue: "Select category", ns: "common" })} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="capacity"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("capacity", { ns: "common" })}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder={t("capacityPlaceholder", { defaultValue: "Max participants", ns: "common" })}
                        {...field}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          field.onChange(isNaN(value) ? 0 : value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="flex justify-end pt-2">
              {/* Annulation explicite : le brouillon est jeté. */}
              <Button
                type="button"
                variant="outline"
                className="mr-2"
                onClick={() => { clearDraft(); onOpenChange(false); }}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" variant="default" disabled={isLoading}>
                {isLoading ? t("saving", { ns: "common" }) : t("save", { ns: "common" })}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
